import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TIERS } from '@/lib/pricing'
import { CHANGELOG } from '@/content/changelog'
import { LandingAtmosphere } from '@/components/LandingAtmosphere'
import { HeroConstellation } from '@/components/HeroConstellation'
import { MarketingShell } from '@/components/MarketingShell'
import {
  fetchPublicSprings,
  fetchPublicTemplates,
  SPRINGS_SNAPSHOT,
  TEMPLATES_SNAPSHOT,
  type PublicSpring,
  type PublicTemplate,
} from '@/api/publicSite'
import { WaitlistSection } from './Waitlist'
import { useRegistrationOpen } from '@/lib/registrationStatus'
import './landing.css'

// The public landing — the founder-picked "B+C synthesis": Direction B
// "Observatory" hero + page spine, Direction C "Instrument" treatment for the
// interior sections. Canon: Diwata-Brand/brand/identity/web_design_language.md

const NOUNS = ['Domains', 'Trove', 'Signals', 'Missions', 'Familiars']

const SYSTEM = [
  { index: '01', name: 'Domains', desc: 'structured operating spaces: one for each thing you keep alive' },
  { index: '02', name: 'Trove', desc: 'springs feed a lake you can actually see: browse it, judge it' },
  { index: '03', name: 'Signals', desc: 'matched to YOUR rules, waiting for YOUR judgment; dismissals teach' },
  { index: '04', name: 'Missions', desc: 'scheduled and triggered work that moves a domain forward' },
  { index: '05', name: 'Familiars', desc: 'labor: native agents or bring-your-own, acting with explicit permission' },
]

// A general-domain example up top (a markets analyst, one of the live /demo
// worlds); the govcon proof lives in Proving Ground, where it's framed.
const READOUT = [
  { label: 'Signal', text: 'Pre-market: a supplier two hops from your watchlist cut its guidance.' },
  { label: 'Context', text: 'It feeds three companies you hold. Your notes flagged the dependency in March.' },
  { label: 'Fit', text: 'Your familiar read the filing: the cut is demand, not timing. Two of your three are exposed.' },
  { label: 'Action', text: 'Review mission opened, cited brief attached → your call at the gate.', action: true },
]

const SHAPE_WORDS: Record<string, string> = {
  people_orgs: 'people & orgs',
  library: 'library',
  pipeline: 'pipeline',
  watch: 'watch',
  tracker: 'tracker',
}

// Reveal-on-scroll: progressive enhancement. Hidden state only exists under
// prefers-reduced-motion: no-preference (CSS); here we flip .is-visible.
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.dwl [data-reveal], .dwl [data-reveal-stagger]')
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-visible'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            io.unobserve(entry.target)
          }
        }
      },
      { threshold: 0.15 },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
}

/** Live registry read with an HONEST fallback — `live: false` renders a labelled snapshot. */
function usePublicFeed<T>(fetcher: () => Promise<T[]>, snapshot: T[]) {
  const [state, setState] = useState<{ rows: T[]; live: boolean }>({ rows: snapshot, live: false })
  useEffect(() => {
    let alive = true
    fetcher()
      .then((rows) => {
        if (!alive) return
        if (rows.length > 0) setState({ rows, live: true })
      })
      .catch(() => {
        /* the snapshot stands, clearly labelled */
      })
    return () => {
      alive = false
    }
  }, [fetcher, snapshot])
  return state
}

function lastDrop(s: PublicSpring): string {
  if (s.last_drop_minutes == null) return '—'
  const m = s.last_drop_minutes
  const age = m < 60 ? `${m}m` : m < 60 * 48 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`
  return s.last_drop_rows != null ? `${s.last_drop_rows.toLocaleString()} rows · ${age}` : age
}

export function Landing() {
  useReveal()

  // ONE story per registration state (founder ruling 2026-08-06): while the
  // doors are closed every CTA on this page is the waitlist; when the flag
  // flips at launch (May 1, 2027), the account CTAs come back. Config, not copy edits.
  const registrationOpen = useRegistrationOpen()

  const springs = usePublicFeed(fetchPublicSprings, SPRINGS_SNAPSHOT)
  const templates = usePublicFeed(fetchPublicTemplates, TEMPLATES_SNAPSHOT)
  const [openTemplate, setOpenTemplate] = useState<string | null>(null)
  const peeked: PublicTemplate | undefined = templates.rows.find((t) => t.key === openTemplate)

  return (
    <MarketingShell>
      <LandingAtmosphere />

      {/* ─── The Observatory hero: the sky above, the copy on the floor ─── */}
      <header className="dwl-hero">
        <HeroConstellation />
        <div className="dwl-hero-copy">
          <p className="dwl-chip">
            <span className="dwl-pip" aria-hidden="true" /> signal → mission · opened at your gate
          </p>
          <h1 className="dwl-title">Diwa Domains</h1>
          <p className="dwl-tagline">One entity, infinite domains.</p>
          <p className="dwl-sub">
            Diwa connects what you already know into one living graph: people, companies,
            catalysts, signals. It flags what just changed and puts a familiar to work on it.
          </p>
          <div className="dwl-cta">
            {registrationOpen ? (
              <Link to="/login" className="dwl-btn">Create account</Link>
            ) : (
              <a href="#threshold" className="dwl-btn">Join the waitlist</a>
            )}
            <Link to="/demo" className="dwl-link">Explore a real world <span className="dwl-arr">→</span></Link>
          </div>
          {!registrationOpen && <p className="dwl-doors-note">Doors open May 1, 2027.</p>}
          <p className="dwl-gate-note">
            <span className="dwl-gm" aria-hidden="true">✦</span> One key across the verse:{' '}
            <b>sign in with your Diwata&nbsp;ID</b>.
          </p>
          <ul className="dwl-nouns" aria-label="The system's five parts">
            {NOUNS.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      </header>

      {/* ─── One Frame — the readout as an engraved instrument plate ─── */}
      <section className="dwl-section" aria-label="Cross-domain insight">
        <div data-reveal>
          <p className="dwl-section-head">One Frame</p>
          <p className="dwl-section-note">One frame holds the insight, the warm path, and the next action.</p>
          <div className="dwl-plate dwl-ticks">
            <span className="dwl-tick-b" aria-hidden="true" />
            <div className="dwl-plate-head">
              <span className="dwl-pip" aria-hidden="true" /> signals · cross-domain readout
            </div>
            {READOUT.map((row) => (
              <div key={row.label} className={`dwl-prow${row.action ? ' dwl-prow--action' : ''}`}>
                <span className="dwl-prow-l">{row.label}</span>
                <p className="dwl-prow-t">{row.text}</p>
              </div>
            ))}
          </div>
          <p className="dwl-plate-caption">Dismiss it, and the rules learn. Accept it, and a familiar goes to work.</p>
        </div>
      </section>

      {/* ─── Now Flowing — the ACTIVE springs as a live ephemeris ─── */}
      <section className="dwl-section dwl-section--tight" aria-label="Live springs">
        <div data-reveal>
          <p className="dwl-section-head">Now Flowing</p>
          <p className="dwl-section-note">
            Every spring is a named, scheduled source. What flows here fills the ledger you judge from.
          </p>
          {!springs.live && (
            <p className="dwl-eph-head">
              <span className="dwl-snapshot-tag">static snapshot · the live registry is resting</span>
            </p>
          )}
          <div className="dwl-eph">
            {springs.rows.slice(0, 8).map((s) => (
              <div className="dwl-eph-row" key={s.name} data-health={s.health}>
                <span className="dwl-eph-name">
                  <span className="dwl-pip" aria-hidden="true" />
                  {s.display}
                </span>
                <span className="dwl-eph-src">{s.carries}</span>
                <span className="dwl-eph-cad">{s.cadence}</span>
                <span className="dwl-eph-last">{springs.live ? lastDrop(s) : '—'}</span>
              </div>
            ))}
          </div>
          <p className="dwl-eph-foot">
            The registry is public. <Link to="/site/springs">see every spring →</Link>
          </p>
        </div>
      </section>

      {/* ─── The Templates — a playable domain gallery ─── */}
      <section className="dwl-section" aria-label="Domain templates">
        <div data-reveal>
          <p className="dwl-section-head">The Templates</p>
          <p className="dwl-section-note">
            A domain is a shape you pour your world into. Open one. These are the real manifests.
          </p>
          {!templates.live && (
            <p className="dwl-eph-head">
              <span className="dwl-snapshot-tag">static snapshot · the live catalog is resting</span>
            </p>
          )}
          <div className="dwl-tpl-grid">
            {templates.rows.map((t) => (
              <button
                key={t.key}
                type="button"
                className="dwl-tpl dwl-ticks"
                aria-expanded={openTemplate === t.key}
                onClick={() => setOpenTemplate((k) => (k === t.key ? null : t.key))}
              >
                <span className="dwl-tick-b" aria-hidden="true" />
                <span className="dwl-tpl-shape">{SHAPE_WORDS[t.shape] ?? t.shape}</span>
                <h3 className="dwl-tpl-name">{t.name}</h3>
                <p className="dwl-tpl-desc">{t.description}</p>
                <span className="dwl-tpl-foot">
                  <b>{t.entity_types.length}</b> nouns
                  {t.springs.length > 0 && (
                    <>
                      {' · '}
                      <b>{t.springs.length}</b> spring{t.springs.length === 1 ? '' : 's'}
                    </>
                  )}
                  {' · '}
                  {openTemplate === t.key ? 'close' : 'open'}
                </span>
              </button>
            ))}
          </div>
          {peeked && (
            <div className="dwl-tpl-peek" role="region" aria-label={`${peeked.name} template detail`}>
              <div className="dwl-tpl-peek-head">
                <span className="dwl-pip" aria-hidden="true" />
                template · <b>{peeked.name}</b> · {SHAPE_WORDS[peeked.shape] ?? peeked.shape}
              </div>
              <div className="dwl-trow">
                <span className="dwl-trow-l">Nouns</span>
                <p className="dwl-trow-t">
                  {peeked.entity_types.map((et) => (
                    <span className="dwl-word" key={et}>
                      <i>{et}</i> <span>· {peeked.words[et]}</span>
                    </span>
                  ))}
                </p>
              </div>
              {peeked.ego_relations.length > 0 && (
                <div className="dwl-trow">
                  <span className="dwl-trow-l">About you</span>
                  <p className="dwl-trow-t">
                    {peeked.ego_relations.map((er) => (
                      <span className="dwl-word" key={er.name}>
                        <i>{er.name}</i> <span>· {er.word}</span>
                      </span>
                    ))}
                  </p>
                </div>
              )}
              {peeked.springs.length > 0 && (
                <div className="dwl-trow">
                  <span className="dwl-trow-l">Springs</span>
                  <p className="dwl-trow-t">
                    {peeked.springs.map((sp) => (
                      <span className="dwl-word" key={sp.name}>
                        <i>{sp.display}</i> <span>· {sp.gloss}</span>
                      </span>
                    ))}
                  </p>
                </div>
              )}
              <div className="dwl-trow">
                <span className="dwl-trow-l">Begin</span>
                <p className="dwl-trow-t">
                  {registrationOpen ? (
                    <Link className="dwl-link" to="/login">
                      Shape it into your own <span className="dwl-arr">→</span>
                    </Link>
                  ) : (
                    <a className="dwl-link" href="#threshold">
                      Join the waitlist to shape your own <span className="dwl-arr">→</span>
                    </a>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── The System — the five parts as a graduated gauge ─── */}
      <section className="dwl-section dwl-section--tight" aria-label="The system">
        <div data-reveal>
          <p className="dwl-section-head">The System</p>
          <p className="dwl-section-note">Five parts, one loop: the graph made actionable.</p>
          <ul className="dwl-gauge" data-reveal-stagger>
            {SYSTEM.map((piece) => (
              <li key={piece.name}>
                <span className="dwl-g-num">{piece.index}</span>
                <span className="dwl-g-name">{piece.name}</span>
                <span className="dwl-g-desc">{piece.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Proving Ground — the live /demo payoff ─── */}
      <section className="dwl-section" aria-label="Proving ground">
        <div data-reveal>
          <p className="dwl-section-head">Proving Ground</p>
          <p className="dwl-section-note">First domain in production: U.S. federal contracting.</p>
          <p className="dwl-prose">
            Official SAM.gov and USASpending pipelines feed a sovereign lake: thousands of awards,
            live solicitations, recompete radar. Your certifications and past performance meet every
            opportunity in a cited fit brief, and a bid/no-bid mission opens at your gate. The
            capability GovWin rents, owned.
          </p>
          <div className="dwl-proof">
            <div className="dwl-proof-copy">
              <p className="dwl-proof-lead">See it for yourself. No account.</p>
              <p className="dwl-proof-sub">
                Three published worlds are open, read-only: a markets analyst, a media researcher,
                and a roofing and HVAC contractor chasing federal work.
                Wander the graph, follow a cross-domain thread, watch what a signal becomes.
              </p>
            </div>
            <Link to="/demo" className="dwl-btn dwl-btn--ghost dwl-proof-btn">Explore a real world →</Link>
          </div>
        </div>
      </section>

      {/* ─── The Log — built in the open ─── */}
      <section className="dwl-section dwl-section--tight" aria-label="Changelog">
        <div data-reveal>
          <p className="dwl-section-head">The Log</p>
          <p className="dwl-section-note">
            Built in the open: what shipped, dated. The surest sign the springs are flowing.
          </p>
          <div className="dwl-log">
            {CHANGELOG.slice(0, 3).map((entry) => (
              <div className="dwl-log-row" key={`${entry.date}-${entry.title}`}>
                <span className="dwl-log-date">{entry.date}</span>
                <span className={`dwl-log-tag${entry.tag === 'new' ? ' dwl-log-tag--new' : ''}`}>
                  {entry.tag ?? 'improved'}
                </span>
                <p className="dwl-log-text">{entry.title} · {entry.points[0]}</p>
              </div>
            ))}
          </div>
          <Link className="dwl-link dwl-log-more" to="/changelog">
            Full changelog <span className="dwl-arr">→</span>
          </Link>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section className="dwl-section" aria-label="Pricing">
        <div data-reveal>
          <p className="dwl-section-head">Pricing</p>
          <p className="dwl-pricing-note">
            {registrationOpen
              ? 'Founding members keep this price for life, locked in.'
              : 'Doors open May 1, 2027. Join the waitlist from any tier and lock founding pricing for life.'}
          </p>
          <div className="dwl-pricing" data-reveal-stagger>
            {TIERS.map((t) => (
              <div key={t.key} className={`dwl-tier dwl-ticks${t.highlight ? ' dwl-tier--highlight' : ''}`}>
                <div className="dwl-tier-head">
                  <h2 className="dwl-tier-name">{t.name}</h2>
                  {t.highlight && <span className="dwl-tier-flag">Popular</span>}
                </div>
                <div className="dwl-tier-price">
                  {t.price}
                  {t.interval && <span>/{t.interval}</span>}
                </div>
                <p className="dwl-tier-tagline">{t.tagline}</p>
                <ul className="dwl-tier-features">
                  {t.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                {registrationOpen ? (
                  <Link
                    to={t.price === 'Custom' ? '/login' : `/login?plan=${encodeURIComponent(t.key)}`}
                    className="dwl-btn"
                  >
                    {t.price === 'Custom' ? 'Contact us' : `Start ${t.name}`}
                  </Link>
                ) : (
                  // Waitlist with plan intent: the join records which tier they
                  // reached for, so the founding price can honor it at open.
                  <Link to={`/waitlist?plan=${encodeURIComponent(t.key)}`} className="dwl-btn">
                    {t.price === '$0' || t.price === 'Custom'
                      ? 'Join the waitlist'
                      : 'Lock the founding price'}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <p className="dwl-pricing-caption">
            Tala's failed runs never bill. Matching, alerts, and NAICS discovery are never metered; only missions are.
          </p>
        </div>
      </section>

      <WaitlistSection />
    </MarketingShell>
  )
}
